import { getUnixTime } from 'date-fns';
import {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
    useCallback,
    useRef,
} from 'react';
import { useQueryBuilder } from './queryBuilderProvider';
import { ICognitoUser } from './provider';

// AWS Amplify v6 imports
import {
    getCurrentUser,
    signOut,
    signInWithRedirect,
    fetchAuthSession,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

// Mock user data for the profile state (not the full ICognitoUser)
interface IProfileUser {
    name: string;
    accessToken: string;
    authTime: number;
    expirationTime: number;
}

// AWS Amplify mock user data structure
interface IAWSUser {
    username: string;
    attributes: {
        email: string;
    };
    signInUserSession?: {
        idToken: {
            payload: {
                name: string;
                family_name: string;
            };
        };
        accessToken: {
            jwtToken: string;
            payload: {
                auth_time: number;
                exp: number;
            };
        };
    };
}

// Auth context value interface
interface IAuthContextValue {
    getProfile: () => IProfileUser;
    handleAuthentication: () => void;
    isAuthenticated: boolean;
    isLoggedIn: boolean;
    login: (loginPath: string) => void;
    logout: () => Promise<void>;
    profile: IProfileUser;
    loginPath: string;
    setLoginPath: (path: string) => void;
}

// Props interface for AuthProvider
interface IAuthProviderProps {
    children: ReactNode;
}

const AuthContext = createContext<IAuthContextValue | undefined>(undefined);

// Initialize user object outside component to avoid dependency issues
const initExpTime = 1893456000;
const initUser: IProfileUser = {
    name: 'notset',
    accessToken: 'notset',
    authTime: initExpTime,
    expirationTime: initExpTime,
};

export function useAuth(): IAuthContextValue {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

/* 
The Auth service that you just created contains functions to deal with different 
steps of the sign in/sign up process. 

The following list briefly summarizes these functions and what they do:

getProfile: This function returns the profile of the logged-in user.
handleAuthentication: This function looks for the result of the authentication process in the URL hash. 
                      Then, the function processes the result with the parseHash method from auth0-js.
isAuthenticated: This function checks whether the expiry time for the user's ID token has passed.
login: This function initiates the login process, redirecting users to the login page.
logout: This function removes the user's tokens and expiry time.
setSession: This function sets the user's ID token, profile, and expiry time.
 */

export const AuthProvider: React.FC<IAuthProviderProps> = ({ children }) => {
    const { userSignIn } = useQueryBuilder();
    const hasInitialized = useRef(false);

    const [profile, setProfile] = useState<IProfileUser>(initUser);
    const [userData, setUserData] = useState<IAWSUser>({} as IAWSUser);
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
    const [loginPath, setLoginPath] = useState<string>('notset');

    const getProfile = (): IProfileUser => profile;

    const handleAuthentication = (): void => {
        console.log('Auth provider: Handling authentication after OAuth callback');
        getCurrentUser()
            .then((user) => {
                if (user) {
                    console.log('Auth provider: User authenticated successfully:', user);
                    setUserData(() => user as unknown as IAWSUser);
                    setIsLoggedIn(true); // Important: Update login state
                } else {
                    console.log('Auth provider: No user found after authentication');
                    setIsLoggedIn(false);
                }
            })
            .catch((error) => {
                console.log(
                    'Auth provider: Error during authentication handling:',
                    error,
                );
                setIsLoggedIn(false);
            });
    };

    const login = async (l: string): Promise<void> => {
        console.log('Auth provider called login', l);

        try {
            // Check if user is already logged in locally
            if (isLoggedIn) {
                console.log('User is already logged in locally, skipping login');
                return;
            }

            // Try to check if user is already authenticated with Amplify
            try {
                const currentUser = await getCurrentUser();
                if (currentUser) {
                    console.log(
                        'User is already authenticated with Amplify, updating local state',
                    );
                    setIsLoggedIn(true);
                    return;
                }
            } catch {
                // User is not authenticated, which is expected for first-time login
                console.log(
                    'User not authenticated (expected for first-time login), proceeding with OAuth redirect',
                );
            }

            // Initiate OAuth login flow
            console.log('Initiating OAuth login flow...');
            await signInWithRedirect({ customState: l });
            console.log('Auth provider called signInWithRedirect successfully');
        } catch (error: unknown) {
            console.log('Auth provider login error:', error);

            // If already authenticated, just update local state
            if (
                error instanceof Error &&
                error.name === 'UserAlreadyAuthenticatedException'
            ) {
                console.log('User already authenticated, updating local state');
                setIsLoggedIn(true);
                return;
            }

            // Don't throw the error here, just log it
            // The user experience should handle this gracefully
        }
    };

    const isAuthenticated = profile.expirationTime - getUnixTime(Date.now()) < 0; // && isLoggedIn;

    useEffect(() => {
        if (!userData || !userData.signInUserSession) {
            setProfile(() => initUser);
            setIsLoggedIn(false);
            return;
        }
        const { payload } = userData.signInUserSession.idToken;
        const name = `${payload.name} ${payload.family_name}`;
        const accessToken = userData.signInUserSession.accessToken.jwtToken;
        console.log('Auth userSign In userData', userData);
        const { auth_time: authTime, exp: expirationTime } =
            userData.signInUserSession.accessToken.payload;
        const newUser: IProfileUser = {
            name,
            accessToken,
            authTime,
            expirationTime,
        };
        setProfile(() => newUser);
        setIsLoggedIn(true);

        // Convert to ICognitoUser format for userSignIn
        const cognitoUser: ICognitoUser = {
            name,
            allUserClients: [], // Will be populated by the provider
            accessToken,
            expirationTime,
            authTime: authTime.toString(),
        };

        (userSignIn as (user: ICognitoUser) => void)(cognitoUser);
    }, [userData, userSignIn]);

    // Initialize authentication state on component mount
    useEffect(() => {
        // Prevent multiple initializations
        if (hasInitialized.current) {
            return;
        }

        const checkAuthState = async () => {
            try {
                const user = await getCurrentUser();
                if (user) {
                    console.log(
                        'AuthProvider: Found existing authenticated user:',
                        user.username,
                    );
                    // For existing authenticated users, we need to manually trigger the sign-in flow
                    // since they might not have the signInUserSession structure
                    setIsLoggedIn(true);

                    try {
                        // For Amplify v6, we need to use fetchAuthSession
                        const authSession = await fetchAuthSession();
                        const accessToken = authSession.tokens?.accessToken?.toString();

                        if (accessToken) {
                            const authTime = Math.floor(Date.now() / 1000);
                            const expirationTime =
                                Math.floor(Date.now() / 1000) + 24 * 60 * 60;

                            // Set profile info with real tokens
                            const basicProfile: IProfileUser = {
                                name: user.username || 'Unknown User',
                                accessToken,
                                authTime,
                                expirationTime,
                            };
                            setProfile(() => basicProfile);

                            // Create cognito user with real access token for API calls
                            const cognitoUser: ICognitoUser = {
                                name: user.username || 'Unknown User',
                                allUserClients: [], // This will be populated by the API call in resetClients
                                accessToken,
                                expirationTime,
                                authTime: authTime.toString(),
                            };

                            console.log(
                                'AuthProvider: Triggering userSignIn for existing user with real token',
                            );
                            (userSignIn as (user: ICognitoUser) => void)(cognitoUser);
                        } else {
                            throw new Error('No access token in session');
                        }
                    } catch (tokenError) {
                        console.error(
                            'AuthProvider: Error getting session tokens:',
                            tokenError,
                        );
                        // Fall back to basic profile without real tokens
                        const basicProfile: IProfileUser = {
                            name: user.username || 'Unknown User',
                            accessToken: 'existing-session-no-token',
                            authTime: Math.floor(Date.now() / 1000),
                            expirationTime: Math.floor(Date.now() / 1000) + 24 * 60 * 60,
                        };
                        setProfile(() => basicProfile);
                    }
                } else {
                    console.log('AuthProvider: No existing authenticated user found');
                }
            } catch (error) {
                console.log('AuthProvider: No existing authentication:', error);
            } finally {
                hasInitialized.current = true; // Mark as initialized regardless of outcome
            }
        };

        checkAuthState();
    }, [userSignIn]); // Keep userSignIn in dependencies but use ref to prevent loops

    const logout = useCallback(async (): Promise<void> => {
        try {
            // First, call AWS Amplify signOut to properly end the SSO session
            console.log('AuthProvider: Signing out from AWS Cognito...');
            await signOut();
            console.log('AuthProvider: Successfully signed out from AWS Cognito');
        } catch (error) {
            console.error('AuthProvider: Error during signOut:', error);
            // Continue with local cleanup even if AWS signOut fails
        }

        // Always clear local state regardless of AWS signOut result
        setProfile(initUser);
        setIsLoggedIn(false);
        console.log('AuthProvider: Local state cleared');
    }, []);

    useEffect(() => {
        const hubListener = (data: {
            payload: { event: string; data?: unknown };
        }): void => {
            const { event, data: payloadData } = data.payload;
            switch (event) {
                case 'signIn':
                    break;
                case 'cognitoHostedUI':
                    handleAuthentication();
                    break;
                case 'signOut':
                    logout();
                    break;
                case 'oAuthSignOut':
                    logout();
                    break;
                case 'signIn_failure':
                case 'parsingCallbackUrl':
                    break;
                case 'tokenRefresh_failure':
                    // Token refresh failures are expected with Cognito hosted UI
                    // The user will need to sign in again when the token expires
                    break;
                case 'cognitoHostedUI_failure':
                    logout();
                    console.error('Auth Sign in failure', payloadData);
                    break;
                case 'customOAuthState':
                    setLoginPath(() => String(payloadData || ''));
                    break;
                default:
                    console.warn(`Auth Unknown event: ${event}`);
            }
        };

        Hub.listen('auth', hubListener);

        // Cleanup function would be:
        // return () => Hub.remove('auth', hubListener);
    }, [logout]);

    const contextValue: IAuthContextValue = {
        getProfile,
        handleAuthentication,
        isAuthenticated,
        isLoggedIn,
        login,
        logout,
        profile,
        loginPath,
        setLoginPath,
    };

    return (
        <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
    );
};
